import { bindFullscreenButton } from './fullscreen';
import type { SessionStatus } from './session';

// The lobby: what the player sees around a match in the online mode. A pure function turns the
// session status into a view (tested), a thin DOM layer shows it. During a match only a banner
// appears, and only while the connection is in trouble.

export type LobbyView =
  | { kind: 'hidden' }
  | { kind: 'banner'; text: string }
  | { kind: 'panel'; text: string; note: string | null; action: LobbyAction | null; cancel: boolean };

export type LobbyAction = 'play' | 'resume' | 'reload';

export interface LobbyContext {
  /** a match is on screen (running, or interrupted and hoped to resume) */
  inMatch: boolean;
  /** the result dialog owns the screen until the player returns to the menu */
  resultVisible?: boolean;
  /** ms since the status was set, for countdowns */
  sinceStatusMs: number;
  /** ms since the last snapshot while playing; null when not playing */
  sinceSnapshotMs: number | null;
  /** no snapshot for this long shows a warning */
  stallWarningMs: number;
  /** a one-line explanation of how the player got back to the lobby */
  note: string | null;
}

function seconds(ms: number): string {
  return `${Math.max(1, Math.ceil(ms / 1000))} s`;
}

export function lobbyView(status: SessionStatus, ctx: LobbyContext): LobbyView {
  if (ctx.resultVisible) return { kind: 'hidden' };
  const panel = (text: string, action: LobbyAction | null = null, cancel = false): LobbyView => ({ kind: 'panel', text, note: ctx.note, action, cancel });
  switch (status.kind) {
    case 'connecting':
      return ctx.inMatch ? { kind: 'banner', text: 'Verbinde neu…' } : panel('Verbinde mit dem Server…');
    case 'offline': {
      const text = `Keine Verbindung zum Server – neuer Versuch in ${seconds(status.retryInMs - ctx.sinceStatusMs)}`;
      return ctx.inMatch ? { kind: 'banner', text } : panel(text);
    }
    case 'resuming':
      return { kind: 'banner', text: 'Verbindung wieder da – das Match geht weiter…' };
    case 'playing':
      if (ctx.sinceSnapshotMs !== null && ctx.sinceSnapshotMs > ctx.stallWarningMs) return { kind: 'banner', text: 'Warte auf den Server…' };
      return { kind: 'hidden' };
    case 'idle':
      return panel('Bereit.', 'play');
    case 'queued':
      return panel('Suche Gegner…', null, true);
    case 'replaced':
      return panel('Diese Sitzung läuft in einem anderen Tab weiter.', 'resume');
    case 'outdated':
      return panel('Der Client ist veraltet – bitte die Seite neu laden.', 'reload');
  }
}

export interface LobbyHandlers {
  onPlay(): void;
  onCancel(): void;
  onResume(): void;
  onReload(): void;
  onLocal(): void;
}

const ACTION_LABEL: Record<LobbyAction, string> = { play: 'Spielen', resume: 'Hier weiterspielen', reload: 'Neu laden' };

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, parent: HTMLElement): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  parent.appendChild(node);
  return node;
}

function setText(node: HTMLElement, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

function setHidden(node: HTMLElement, hidden: boolean): void {
  node.classList.toggle('hidden', hidden);
}

export function createLobby(root: HTMLElement, handlers: LobbyHandlers) {
  const panel = el('div', 'hud-lobby hidden', root);
  const box = el('div', 'hud-lobby-box', panel);
  el('div', 'hud-lobby-title', box).textContent = 'chessshooter';
  const status = el('div', 'hud-lobby-status', box);
  const note = el('div', 'hud-lobby-note hidden', box);
  const buttons = el('div', 'hud-lobby-buttons', box);
  const action = el('button', 'hud-primary', buttons);
  action.type = 'button';
  const cancel = el('button', 'hud-secondary', buttons);
  cancel.type = 'button';
  cancel.textContent = 'Abbrechen';
  const fullscreen = el('button', 'hud-link', box);
  fullscreen.type = 'button';
  bindFullscreenButton(fullscreen);
  const local = el('button', 'hud-link', box);
  local.type = 'button';
  local.textContent = 'Lokal gegen den Bot spielen';
  const banner = el('div', 'hud-banner hidden', root);

  let currentAction: LobbyAction | null = null;
  action.addEventListener('click', () => {
    if (currentAction === 'play') handlers.onPlay();
    else if (currentAction === 'resume') handlers.onResume();
    else if (currentAction === 'reload') handlers.onReload();
  });
  cancel.addEventListener('click', () => handlers.onCancel());
  local.addEventListener('click', () => handlers.onLocal());

  function render(view: LobbyView): void {
    setHidden(panel, view.kind !== 'panel');
    setHidden(banner, view.kind !== 'banner');
    if (view.kind === 'banner') setText(banner, view.text);
    if (view.kind !== 'panel') return;
    setText(status, view.text);
    setHidden(note, view.note === null);
    if (view.note !== null) setText(note, view.note);
    currentAction = view.action;
    setHidden(action, view.action === null);
    if (view.action) setText(action, ACTION_LABEL[view.action]);
    setHidden(cancel, !view.cancel);
  }

  return { render };
}
